// File: src/components/SubcategoryCard.jsx
// Individual subcategory card with 2x2 product thumbnail grid
// Routes to: /subcategory/:slug
// Part of AllCategoriesSection → CategoryRowSection hierarchy

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const SubcategoryCard = React.memo(({ subcategory }) => {
  const [hoveredProductId, setHoveredProductId] = useState(null);

  // Validate subcategory has required data (should have 4 products)
  if (!subcategory || !subcategory.products || subcategory.products.length === 0) {
    return null;
  }

  // Ensure we always show exactly 4 products (pad with placeholders if needed)
  const displayProducts = [...subcategory.products.slice(0, 4)];
  while (displayProducts.length < 4) {
    displayProducts.push({
      id: `placeholder-${displayProducts.length}`,
      name: 'Coming Soon',
      imageUrl: '/api/placeholder/300/300',
      isPlaceholder: true
    });
  }

  return (
    <Link
      to={`/subcategory/${subcategory.slug}`}
      className="block bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 group transform hover:-translate-y-1"
    >
      {/* Card Header - Subcategory Name */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h4 className="text-sm font-semibold text-gray-900 line-clamp-1 group-hover:text-blue-600 transition-colors duration-200">
          {subcategory.name}
        </h4>
      </div>

      {/* 2x2 Product Thumbnail Grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3">
          {displayProducts.map((product) => (
            <div
              key={product.productId || product.id} // Use productId for real products, id for placeholders
              className="relative"
              onMouseEnter={() => !product.isPlaceholder && setHoveredProductId(product.id)}
              onMouseLeave={() => setHoveredProductId(null)}
            >
              {/* Product Image Container - 1:1 Aspect Ratio */}
              <div 
                className="relative overflow-hidden bg-gray-50 rounded transition-all duration-300"
                style={{ aspectRatio: '1 / 1' }}
              >
                <img
                  src={product.imageUrls?.[0] || product.imageUrl || '/api/placeholder/300/300'}
                  alt={product.name}
                  className={`w-full h-full object-cover transition-transform duration-300 ${
                    !product.isPlaceholder ? 'group-hover:scale-105' : 'opacity-50'
                  }`}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.target.src = '/api/placeholder/300/300';
                    e.target.alt = 'Product image unavailable';
                  }}
                />
                
                {/* Subtle hover overlay */}
                {hoveredProductId === product.id && (
                  <div className="absolute inset-0 bg-black/5 transition-opacity duration-300" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer CTA - See All Link */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <div className="text-xs font-semibold text-blue-600 group-hover:text-blue-700 transition-colors duration-200 inline-flex items-center gap-1">
          See all in {subcategory.name}
          <span className="text-sm transition-transform duration-200 group-hover:translate-x-1">›</span>
        </div>
      </div>
    </Link>
  );
});

SubcategoryCard.displayName = 'SubcategoryCard';

export default SubcategoryCard;
